(() => {
  'use strict';

  const selectors = {
    container: '.boost-sd__instant-search-container, .boost-sd__search-widget-init-container',
    input: '.boost-sd__search-widget-init-input, [data-ppa-boost-search] input[type="search"]',
    wrapper: '#boost-sd__search-widget-init-wrapper',
    product: '.boost-sd__suggestion-queries-item--product',
    sku: '.boost-sd__suggestion-queries-item-sku',
    viewAll: '.boost-sd__view-all-link, .boost-sd__view-all a',
    autocomplete: '.boost-sd__instant-search-autocomplete',
    otherColumn: '.boost-sd__instant-search-column--others',
    productColumn: '.boost-sd__instant-search-column--products',
    zeroProductList: '.boost-sd__instant-search-product-list-on-click',
  };

  let frame;
  let boostObserver;
  let discoveryObserver;
  let resultsObserver;
  let resultsRootObserver;
  let observedResultsRoot;
  let resetOnOpen = false;
  let mobileSearchActive = false;
  let searchMenuActive = false;
  let searchOverlayActive = false;
  let searchDismissed = false;
  let closingSearch = false;
  let trackedInput = null;
  let instantBusinessPriceFilterActive = false;
  let resultsBusinessPriceFilterActive = false;
  let businessPriceDefaultInitialised = false;
  const productPriceCache = new Map();
  const productMetadataCache = new Map();
  let resultsFrame;
  const b2bMetadataContext = document.getElementById('ppa-b2b-business-price-config');
  let b2bMetadataKey = '';
  try {
    b2bMetadataKey = JSON.parse(b2bMetadataContext?.textContent || '{}').cacheKey || '';
  } catch (_error) {}
  const b2bProductRequests = b2bMetadataKey
    ? (window.PpaB2BProductRequests ||= new Map()) : null;
  const isResultsPage = ['collection', 'search'].includes(window.current?.template?.name);

  const searchPlaceholder = () =>
    window.matchMedia('(max-width: 989px)').matches ? 'Search products or SKUs' : 'Search products, SKUs or categories';

  const syncSearchControl = (input) => {
    const search = input?.closest('[data-ppa-boost-search]');
    if (!search) return;

    const hasQuery = Boolean(input.value.trim());
    const button = search.querySelector('.search__button');
    const boostIsReady =
      input.classList.contains('boost-sd__search-widget-init-input') &&
      Boolean(document.querySelector(selectors.wrapper));
    const canClearInstantSearch = hasQuery && boostIsReady;
    search.classList.toggle('ppa-search-has-query', canClearInstantSearch);

    if (!button) return;
    button.type = canClearInstantSearch ? 'button' : 'submit';
    button.setAttribute('aria-label', canClearInstantSearch ? 'Clear search term' : 'Search');
  };

  const queueUpdate = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      updateSearchInterface();
    });
  };

  const setPageScrollLocked = (locked) => {
    document.documentElement.classList.toggle('ppa-boost-search-scroll-lock', locked);
    document.body.classList.toggle('ppa-boost-search-scroll-lock', locked);
  };

  const searchWrapper = () => {
    const wrappers = Array.from(document.querySelectorAll(selectors.wrapper));
    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    const responsiveWrappers = wrappers.filter((wrapper) =>
      isMobile
        ? wrapper.querySelector('.boost-sd__instant-search-results--mobile')
        : !wrapper.querySelector('.boost-sd__instant-search-results--mobile')
    );
    const candidates = responsiveWrappers.length ? responsiveWrappers : wrappers;
    return (
      candidates.find((wrapper) => !wrapper.classList.contains('boost-sd__g-hide') && wrapper.getClientRects().length) ||
      candidates.find((wrapper) => wrapper.style.position === 'absolute' || wrapper.style.position === 'fixed') ||
      candidates[candidates.length - 1]
    );
  };

  const activeInput = () => {
    const focusedInput = document.activeElement?.matches?.(selectors.input) ? document.activeElement : null;
    return (
      focusedInput ||
      (trackedInput?.isConnected ? trackedInput : null) ||
      Array.from(document.querySelectorAll(selectors.input)).find((input) => input.getClientRects().length)
    );
  };

  const enhanceSearchInputs = () => {
    document.querySelectorAll(selectors.input).forEach((input) => {
      input.setAttribute('placeholder', searchPlaceholder());
      syncSearchControl(input);
    });
  };

  const normaliseSku = (value) =>
    value
      .replace(/^sku\s*:\s*/i, '')
      .replace(/\s+/g, '')
      .toUpperCase();

  const normaliseSaleBadges = (root = document) => {
    root
      .querySelectorAll(
        '.boost-sd__suggestion-queries-item-badge--sale, .boost-sd__product-label--sale'
      )
      .forEach((badge) => {
        badge.classList.add('ppa-sale-badge');

        const label =
          badge.querySelector('.boost-sd__product-label-text > span') ||
          badge.querySelector(':scope > span') ||
          badge;

        const labelText = label.textContent?.trim() || '';
        const ariaLabel = badge.getAttribute('aria-label') || '';
        const title = badge.getAttribute('title') || '';

        if (/^sale$/i.test(labelText) && labelText !== 'SALE') label.textContent = 'SALE';
        if (/^sale$/i.test(ariaLabel) && ariaLabel !== 'SALE') badge.setAttribute('aria-label', 'SALE');
        if (/^sale$/i.test(title) && title !== 'SALE') badge.setAttribute('title', 'SALE');
      });
  };

  const tagResultLinks = (wrapper) => {
    wrapper.querySelectorAll('a').forEach((link) => {
      if (link.closest('.ppa-boost-search__trustpilot')) {
        link.dataset.ppaSearchAction = 'product-review';
      } else if (link.closest(selectors.product)) {
        link.dataset.ppaSearchAction = 'product-result';
      } else if (link.matches(selectors.viewAll) || link.closest('.boost-sd__view-all')) {
        link.dataset.ppaSearchAction = 'view-all';
      } else if (link.closest('.boost-sd__suggestion-queries-item')) {
        link.dataset.ppaSearchAction = 'autocomplete-suggestion';
      }
    });

    wrapper.querySelectorAll('.boost-sd__suggestion-queries-group').forEach((group) => {
      const heading = group.querySelector('.boost-sd__suggestion-queries-group-title')?.textContent?.trim().toLowerCase() || '';
      const action = heading.includes('popular')
        ? 'popular-search'
        : heading.includes('recent')
          ? 'recent-search'
          : null;

      if (!action) return;
      group.querySelectorAll('a').forEach((link) => {
        link.dataset.ppaSearchAction = action;
      });
    });
  };

  const cloneTemplate = (selector) => document.querySelector(selector)?.content.cloneNode(true) || null;

  const b2bSearchText = () => {
    const template = document.querySelector('[data-ppa-b2b-search-config]');
    const script = template?.content?.querySelector('[data-ppa-b2b-search-text]');
    try {
      return JSON.parse(script?.textContent || '{}');
    } catch (_error) {
      return {};
    }
  };

  const renderFrequentlyOrderedProducts = (section) => {
    if (!section) return false;
    const template = document.querySelector('[data-ppa-search-frequent-products-template]');
    const dataNode = template?.content?.querySelector('[data-ppa-frequent-order-data]');
    const fallbackNode = template?.content?.querySelector('[data-ppa-business-price-products]');
    const list = section.querySelector('[data-ppa-frequent-product-list]');
    if (!dataNode || !list) return false;

    const parseData = (node) => {
      try {
        return JSON.parse(node?.textContent || '[]');
      } catch (_error) {
        return [];
      }
    };

    const orderItems = parseData(dataNode).filter((item) => item?.businessPrice === true);
    const fallbackItems = parseData(fallbackNode).filter((item) => item?.businessPrice === true);
    const products = new Map();

    [...orderItems, ...fallbackItems].forEach((item) => {
      if (!item?.productId || !item?.url) return;
      const key = String(item.productId);
      const itemDate = Number(item.date) || 0;
      const current = products.get(key) || {
        productId: key,
        title: item.title || '',
        sku: item.sku || '',
        url: item.url,
        image: item.image || '',
        available: item.available !== false,
        businessPrice: item.businessPrice === true,
        price: Number(item.price) || 0,
        compareAt: Number(item.compareAt) || 0,
        latestDate: 0,
        quantity: 0,
        orderIds: new Set(),
      };

      current.businessPrice = current.businessPrice || item.businessPrice === true;
      current.quantity += Math.max(0, Number(item.quantity) || 0);
      if (item.orderId || itemDate) current.orderIds.add(String(item.orderId || itemDate));
      if (itemDate >= current.latestDate || current.latestDate === 0) {
        current.title = item.title || current.title;
        current.sku = item.sku || current.sku;
        current.url = item.url || current.url;
        current.image = item.image || current.image;
        current.available = item.available !== false;
        current.price = Number(item.price) || current.price;
        current.compareAt = Number(item.compareAt) || current.compareAt;
      }
      current.latestDate = Math.max(current.latestDate, itemDate);
      products.set(key, current);
    });

    const frequent = Array.from(products.values())
      .filter((product) => product.businessPrice)
      .sort((left, right) =>
        right.orderIds.size - left.orderIds.size ||
        right.quantity - left.quantity ||
        right.latestDate - left.latestDate ||
        left.title.localeCompare(right.title)
      )
      .slice(0, 5);

    if (!frequent.length) {
      section.remove();
      return false;
    }

    const text = b2bSearchText();
    const fragment = document.createDocumentFragment();
    frequent.forEach((product) => {
      const item = document.createElement('li');
      item.className = 'ppa-boost-search__best-seller ppa-boost-search__frequent-product';
      item.dataset.productId = product.productId;

      const link = document.createElement('a');
      link.className = 'ppa-boost-search__best-seller-link';
      link.href = product.url;
      link.dataset.ppaSearchAction = 'frequently-ordered-product';

      const media = document.createElement('span');
      media.className = 'ppa-boost-search__best-seller-media';
      if (product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 80;
        image.height = 80;
        media.append(image);
      }

      const info = document.createElement('span');
      info.className = 'ppa-boost-search__best-seller-info';

      const title = document.createElement('span');
      title.className = 'ppa-boost-search__best-seller-title';
      title.textContent = product.title;
      info.append(title);

      if (product.sku) {
        const sku = document.createElement('span');
        sku.className = 'ppa-boost-search__best-seller-sku';
        sku.textContent = `SKU: ${product.sku}`;
        info.append(sku);
      }

      const businessPriceRow = document.createElement('span');
      businessPriceRow.className = 'ppa-boost-search__business-price-row';

      const businessPriceLabel = document.createElement('span');
      businessPriceLabel.className = 'ppa-b2b-business-price-label';
      businessPriceLabel.textContent = text.businessPrice || (document.body.classList.contains('reseller-customer') ? 'Your Reseller Price' : 'Your business price');
      businessPriceRow.append(businessPriceLabel);

      if (product.price > 0) {
        const price = document.createElement('strong');
        price.textContent = formatPrice(product.price / 100);
        businessPriceRow.append(price);
      }
      info.append(businessPriceRow);

      const frequency = document.createElement('span');
      frequency.className = 'ppa-boost-search__frequency';
      if (product.orderIds.size > 1) {
        frequency.textContent = (text.orderedMany || 'Ordered in __COUNT__ recent orders')
          .replace('__COUNT__', product.orderIds.size);
      } else if (product.orderIds.size === 1) {
        frequency.textContent = text.orderedOnce || 'Ordered once recently';
      } else {
        frequency.textContent = text.businessPriceAvailable || (document.body.classList.contains('reseller-customer') ? 'Available with your reseller price' : 'Available with your business price');
      }
      info.append(frequency);

      if (product.latestDate) {
        const date = document.createElement('span');
        date.className = 'ppa-boost-search__last-ordered';
        const formatted = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
          .format(new Date(product.latestDate * 1000));
        date.textContent = (text.lastOrdered || 'Last ordered __DATE__').replace('__DATE__', formatted);
        info.append(date);
      }

      const stock = document.createElement('span');
      stock.className = 'ppa-boost-search__best-seller-stock';
      stock.innerHTML = '<span aria-hidden="true"></span>';
      stock.append(document.createTextNode(product.available ? 'In stock' : 'Unavailable'));
      info.append(stock);

      link.append(media, info);
      item.append(link);
      fragment.append(item);
    });

    list.replaceChildren(fragment);
    return true;
  };

  const showB2BQuickLinks = (wrapper, state) => {
    const template = document.querySelector('[data-ppa-b2b-quick-links-template]');
    if (!template) return;

    wrapper.querySelectorAll('.boost-sd__suggestion-queries-group').forEach((group) => {
      const heading = group
        .querySelector('.boost-sd__suggestion-queries-group-title, .boost-sd__suggestion-queries-header, .boost-sd__instant-search-column-title')
        ?.textContent?.trim().toLowerCase() || '';
      if (heading.includes('popular')) group.hidden = state === 'ready';
    });

    wrapper.querySelector('.ppa-boost-search__quick-links')?.remove();
    if (state !== 'ready') return;

    const otherColumn = wrapper.querySelector(selectors.otherColumn);
    const content = cloneTemplate('[data-ppa-b2b-quick-links-template]');
    if (otherColumn && content) otherColumn.append(content);
  };

  const updateInstantBusinessPriceState = (wrapper) => {
    if (!wrapper.matches?.(selectors.wrapper) || !document.querySelector('[data-ppa-b2b-search-config]')) return;

    const products = Array.from(wrapper.querySelectorAll(selectors.product)).filter(
      (product) => !product.closest(selectors.zeroProductList)
    );
    const state = wrapper.dataset.ppaSearchState === 'typed'
      ? businessPriceStatus(products, instantBusinessPriceFilterActive)
      : '';
    const template = document.querySelector('[data-ppa-search-business-price-empty-template]');
    const productGroup = window.matchMedia('(max-width: 989px)').matches
      ? wrapper.querySelector('.boost-sd__instant-search-results--mobile .boost-sd__instant-search-product-list')
      : wrapper.querySelector(`${selectors.productColumn} .boost-sd__instant-search-group`);
    if (!productGroup) return;

    let empty = wrapper.querySelector('[data-ppa-search-business-price-empty]');
    if (state && !empty) {
      const content = cloneTemplate('[data-ppa-search-business-price-empty-template]');
      empty = content?.querySelector('[data-ppa-search-business-price-empty]');
      if (empty) productGroup.append(empty);
    }
    if (empty) {
      const viewAll = productGroup.querySelector(':scope > .boost-sd__view-all');
      if (viewAll && empty.nextElementSibling !== viewAll) viewAll.before(empty);
      updateBusinessPriceMessage(empty, template, state);
    }
  };

  const applyInstantBusinessPriceFilter = (wrapper) => {
    initialiseBusinessPriceDefault();
    wrapper.querySelectorAll('[data-ppa-business-price-only]').forEach((checkbox) => {
      checkbox.checked = instantBusinessPriceFilterActive;
    });
    wrapper.querySelectorAll(selectors.product).forEach((product) => {
      product.classList.toggle(
        'ppa-business-price-filtered',
        instantBusinessPriceFilterActive && product.dataset.ppaBusinessPrice !== 'true'
      );
    });
    updateInstantBusinessPriceState(wrapper);
  };

  const businessPriceDefault = () =>
    document.querySelector('[data-ppa-b2b-search-config][data-ppa-business-price-default="true"]');

  const initialiseBusinessPriceDefault = () => {
    if (businessPriceDefaultInitialised) return;
    const config = businessPriceDefault();
    if (!config) return;

    businessPriceDefaultInitialised = true;
    const active = window.PpaBusinessPriceScope?.active ?? false;
    instantBusinessPriceFilterActive = false;
    resultsBusinessPriceFilterActive = active;
  };

  const rememberBusinessPricePreference = () => {
    const config = businessPriceDefault();
    if (!config || window.PpaBusinessPriceScope?.automaticDefault) return;

    try {
      window.sessionStorage.setItem(
        config.dataset.businessPricePreferenceKey,
        resultsBusinessPriceFilterActive ? 'business' : 'all'
      );
    } catch (error) {
      return;
    }
  };

  const setInstantBusinessPriceFilter = (active) => {
    initialiseBusinessPriceDefault();
    instantBusinessPriceFilterActive = active;
    document.querySelectorAll(selectors.wrapper).forEach(applyInstantBusinessPriceFilter);
  };

  const setResultsBusinessPriceFilter = (active) => {
    resultsBusinessPriceFilterActive = active;
    rememberBusinessPricePreference();
    window.PpaBusinessPriceScope?.setActive(active);
    applyResultsBusinessPriceFilter();
  };

  const businessPriceStatus = (products, active) => {
    if (!active || products.length === 0) return '';
    if (products.some((product) => product.dataset.ppaBusinessPrice === 'error')) return 'error';
    if (products.some((product) => !['true', 'false'].includes(product.dataset.ppaBusinessPrice))) return 'loading';
    const visible = products.some((product) =>
      !product.hidden && product.getAttribute('aria-hidden') !== 'true' &&
      !product.classList.contains('ppa-business-price-filtered')
    );
    return visible ? '' : 'empty';
  };

  const updateBusinessPriceMessage = (message, template, state) => {
    if (!message || !template) return;
    const heading = message.querySelector('h2, h3');
    const copy = message.querySelector('p');
    const original = template.content.querySelector('section');
    const headingText = state === 'loading' || state === 'error'
      ? template.dataset[state + 'Heading']
      : original?.querySelector('h2, h3')?.textContent;
    const copyText = state === 'loading' || state === 'error'
      ? template.dataset[state + 'Copy']
      : original?.querySelector('p')?.textContent;
    if (heading && heading.textContent !== headingText) heading.textContent = headingText || '';
    if (copy && copy.textContent !== copyText) copy.textContent = copyText || '';
    message.hidden = !state;
  };

  const resultsBusinessPriceCheckbox = () =>
    document.querySelector('[data-ppa-results-business-price-only]');

  const updateResultsBusinessPriceState = () => {
    const template = document.querySelector('[data-ppa-results-business-price-empty-template]');
    const results = document.querySelector('.boost-sd__filter-product-list') ||
      document.querySelector('#MainContent .boost-sd-right');
    const productList = results?.querySelector('.boost-sd__product-list');
    if (!template || !results || (!productList && !window.PpaBusinessPriceScope)) return;

    const cards = Array.from(productList?.children || []).filter((card) => card.matches('.boost-sd__product-item'));
    const globalScope = window.PpaBusinessPriceScope;
    const globalFiltering = resultsBusinessPriceFilterActive && Boolean(globalScope);
    const pageVisibleCount = cards.filter(
      (card) => !card.hidden && card.getAttribute('aria-hidden') !== 'true' &&
        !card.classList.contains('ppa-business-price-filtered')
    ).length;
    const visibleCount = globalFiltering && globalScope.status === 'ready'
      ? globalScope.total : pageVisibleCount;
    const state = globalFiltering
      ? (globalScope.status === 'ready' ? (globalScope.total === 0 ? 'empty' : '') : globalScope.status)
      : businessPriceStatus(cards, resultsBusinessPriceFilterActive);
    results.querySelectorAll('.boost-sd__pagination, .boost-sd__load-more').forEach((pagination) => {
      if (globalFiltering && state) {
        pagination.dataset.ppaBusinessPriceHidden = 'true';
        pagination.hidden = true;
      } else if (pagination.dataset.ppaBusinessPriceHidden) {
        delete pagination.dataset.ppaBusinessPriceHidden;
        pagination.hidden = false;
      }
    });
    let empty = results.querySelector('[data-ppa-results-business-price-empty]');
    // Loading is an inline count, not a full-width empty-result panel.
    const messageState = state === 'loading' ? '' : state;
    if (messageState && !empty) {
      empty = template.content.querySelector('[data-ppa-results-business-price-empty]')?.cloneNode(true);
      if (empty) {
        if (productList) productList.before(empty);
        else results.append(empty);
      }
    }
    updateBusinessPriceMessage(empty, template, messageState);
    const nativeEmpty = results.querySelector('.boost-sd__product-list-pagination-in-collection-search-empty-message');
    if (nativeEmpty) nativeEmpty.hidden = globalFiltering && Boolean(state);

    document.querySelectorAll('#MainContent .boost-sd__product-count').forEach((nativeCount) => {
      let filteredCount = nativeCount.previousElementSibling;
      if (!filteredCount?.matches('[data-ppa-results-business-price-count]')) {
        if (!resultsBusinessPriceFilterActive) return;
        filteredCount = document.createElement('span');
        filteredCount.className = 'ppa-results-business-price-count';
        filteredCount.dataset.ppaResultsBusinessPriceCount = '';
        filteredCount.setAttribute('role', 'status');
        filteredCount.setAttribute('aria-live', 'polite');
        nativeCount.before(filteredCount);
      }
      const text = state === 'loading' ? template.dataset.resultsLoading : state === 'error' ? template.dataset.errorHeading :
        (visibleCount === 1 ? template.dataset.countOne : template.dataset.countMany)
          ?.replace('__COUNT__', String(visibleCount)) || '';
      if (filteredCount.textContent !== text) filteredCount.textContent = text;
      filteredCount.hidden = !resultsBusinessPriceFilterActive;
      nativeCount.classList.toggle('ppa-results-native-count--filtered', resultsBusinessPriceFilterActive);
    });
  };

  const applyResultsBusinessPriceFilter = () => {
    document.querySelectorAll('[data-ppa-results-business-price-only]').forEach((checkbox) => {
      checkbox.checked = resultsBusinessPriceFilterActive;
    });
    document.querySelectorAll('.boost-sd__filter-product-list .boost-sd__product-item').forEach((card) => {
      const scope = window.PpaBusinessPriceScope;
      // Keep already verified cards usable while the complete result set is loading.
      const verified = scope?.status === 'loading' &&
        (scope.includes(card.dataset.productId || productCardData(card).id) || card.dataset.ppaBusinessPrice === 'true');
      card.classList.toggle(
        'ppa-business-price-filtered',
        resultsBusinessPriceFilterActive && (scope
          ? scope.status !== 'ready' && !verified
          : card.dataset.ppaBusinessPrice !== 'true')
      );
    });
    updateResultsBusinessPriceState();
  };

  window.addEventListener('ppa-business-price-scope', () => {
    if (!window.PpaBusinessPriceScope) return;
    resultsBusinessPriceFilterActive = window.PpaBusinessPriceScope.active;
    applyResultsBusinessPriceFilter();
  });

  const ensureResultsBusinessPriceFilter = () => {
    initialiseBusinessPriceDefault();
    const template = document.querySelector('[data-ppa-b2b-search-config]');
    const filterTrees = document.querySelectorAll('.boost-sd__filter-tree-vertical');
    if (!template) return;

    filterTrees.forEach((filterTree) => {
      let filter = filterTree.querySelector('[data-ppa-results-business-price-filter]');
      if (!filter) {
        const label =
          document.querySelector('[data-ppa-search-refine-template] [data-ppa-business-price-only]')?.nextElementSibling?.textContent?.trim() ||
          (document.body.classList.contains('reseller-customer') ? 'Show only products with your reseller price' : 'Show only products with your business price');
        const heading =
          document.querySelector('[data-ppa-search-refine-template] .ppa-boost-search__business-price-filter h3')?.textContent?.trim() ||
          (document.body.classList.contains('reseller-customer') ? 'Reseller pricing' : 'Business pricing');

        filter = document.createElement('section');
        filter.className = 'ppa-results-business-price-filter';
        filter.dataset.ppaResultsBusinessPriceFilter = '';
        filter.innerHTML =
          '<h3></h3><label><input type="checkbox" data-ppa-results-business-price-only><span></span></label>';
        filter.querySelector('h3').textContent = heading;
        filter.querySelector('span').textContent = label;
        filterTree.prepend(filter);
      }

      const checkbox = filter.querySelector('[data-ppa-results-business-price-only]');
      if (checkbox) checkbox.checked = resultsBusinessPriceFilterActive;
    });
  };

  const updateContentPriority = (wrapper) => {
    let hasEditorialResults = false;

    wrapper.querySelectorAll('.boost-sd__suggestion-queries-group').forEach((group) => {
      group.classList.remove('ppa-boost-search__editorial-group');
      group.classList.toggle(
        'ppa-boost-search__empty-group',
        !group.querySelector('a, button, input')
      );
      const heading = group
        .querySelector('.boost-sd__suggestion-queries-group-title, .boost-sd__suggestion-queries-header, .boost-sd__instant-search-column-title')
        ?.textContent?.trim()
        .toLowerCase();

      if (heading?.includes('blog & pages') && group.querySelector('a')) {
        group.classList.add('ppa-boost-search__editorial-group');
        hasEditorialResults = true;
      }
    });

    const products = Array.from(wrapper.querySelectorAll(selectors.product)).filter(
      (product) => !product.closest(selectors.zeroProductList)
    );
    const hasProducts = products.length > 0;

    wrapper.dataset.ppaSearchProductCount = String(products.length);
    wrapper.dataset.ppaSearchDensity = hasProducts && products.length <= 3 ? 'compact' : 'standard';

    wrapper.dataset.ppaSearchContent = hasEditorialResults ? 'editorial' : 'catalogue';
    wrapper.dataset.ppaSearchLayout = hasEditorialResults
      ? hasProducts
        ? 'editorial-mixed'
        : 'editorial-only'
      : 'catalogue';
  };

  const resetSearchScroll = (wrapper) => {
    wrapper.scrollTop = 0;
    wrapper
      .querySelectorAll(
        '.boost-sd__instant-search-results, .boost-sd__instant-search-autocomplete, .boost-sd__instant-search-column, .boost-sd__instant-search-group, .boost-sd__instant-search-product-list-items'
      )
      .forEach((element) => {
        element.scrollTop = 0;
        element.scrollLeft = 0;
      });
  };

  const placeViewAllAtEnd = (wrapper) => {
    const viewAll = wrapper.querySelector('.boost-sd__view-all');
    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    const productGroup = isMobile
      ? wrapper.querySelector('.boost-sd__instant-search-results--mobile .boost-sd__instant-search-product-list')
      : wrapper.querySelector(`${selectors.productColumn} .boost-sd__instant-search-group`);

    if (productGroup && viewAll && viewAll.parentElement !== productGroup) {
      productGroup.append(viewAll);
    }
  };

  const ensurePanelClose = (wrapper) => {
    if (wrapper.querySelector('.ppa-boost-search__panel-close')) return;

    const button = document.createElement('button');
    const translatedLabel = document.querySelector('.ppa-boost-search__close')?.getAttribute('aria-label');
    button.type = 'button';
    button.className = 'ppa-boost-search__panel-close';
    button.setAttribute('aria-label', translatedLabel || 'Close search');
    button.textContent = '\u00d7';
    wrapper.prepend(button);
  };

  const arrangeSearchColumns = (wrapper) => {
    const autocomplete = wrapper.querySelector(selectors.autocomplete);
    const otherColumn = autocomplete?.querySelector(selectors.otherColumn);
    const productColumn = autocomplete?.querySelector(selectors.productColumn);
    if (!autocomplete || !otherColumn || !productColumn) return;

    if (window.matchMedia('(max-width: 989px)').matches) {
      if (productColumn.nextElementSibling !== otherColumn) autocomplete.insertBefore(productColumn, otherColumn);
      return;
    }

    if (otherColumn.nextElementSibling !== productColumn) autocomplete.insertBefore(otherColumn, productColumn);
  };

  const addRefinementLinks = (wrapper, state) => {
    if (state !== 'typed' || wrapper.dataset.ppaSearchLayout === 'editorial-only') {
      wrapper
        .querySelectorAll(
          '.ppa-boost-search__mobile-filters, .ppa-boost-search__price-filter, .ppa-boost-search__refine'
        )
        .forEach((item) => item.remove());
      wrapper.querySelectorAll('[data-ppa-created-refinement-column]').forEach((column) => column.remove());
      return;
    }

    const autocomplete = wrapper.querySelector(selectors.autocomplete);
    if (!autocomplete) return;

    const isMobile = window.matchMedia('(max-width: 989px)').matches;

    let column = autocomplete.querySelector(selectors.otherColumn);
    if (!column) {
      column = document.createElement('aside');
      column.className = 'boost-sd__instant-search-column boost-sd__instant-search-column--others ppa-boost-search__refinement-column';
      column.dataset.ppaCreatedRefinementColumn = '';
      autocomplete.prepend(column);
    }

    const productColumn = autocomplete.querySelector(selectors.productColumn);
    const mobileProductList = autocomplete.querySelector('.boost-sd__instant-search-product-list');
    const mobileFilterHost = productColumn || mobileProductList?.parentElement;
    const mobileResultsBlock =
      productColumn?.querySelector(':scope > .boost-sd__instant-search-group') || mobileProductList;
    const existingPriceFilter = wrapper.querySelector('.ppa-boost-search__price-filter');
    const existingRefines = Array.from(wrapper.querySelectorAll('.ppa-boost-search__refine'));
    const existingMobileFilters = wrapper.querySelector('.ppa-boost-search__mobile-filters');
    const refinementIsReady = isMobile
      ? Boolean(
          mobileFilterHost &&
            existingMobileFilters?.contains(existingPriceFilter) &&
            existingRefines.length > 0 &&
            existingRefines.every((refine) => existingMobileFilters.contains(refine)) &&
            existingMobileFilters.parentElement === mobileFilterHost &&
            mobileFilterHost.firstElementChild === existingMobileFilters &&
            (!mobileResultsBlock || existingMobileFilters.nextElementSibling === mobileResultsBlock)
        )
      : Boolean(
          existingPriceFilter?.parentElement === column &&
            existingRefines.length > 0 &&
            existingRefines.every((refine) => refine.parentElement === column) &&
            !existingMobileFilters
        );

    if (refinementIsReady) return;

    wrapper
      .querySelectorAll(
        '.ppa-boost-search__mobile-filters, .ppa-boost-search__price-filter, .ppa-boost-search__refine'
      )
      .forEach((item) => item.remove());

    const content = cloneTemplate('[data-ppa-search-refine-template]');
    if (!content) return;

    const priceFilter = content.querySelector('.ppa-boost-search__price-filter');
    const refinements = Array.from(content.querySelectorAll('.ppa-boost-search__refine'));

    if (isMobile && mobileFilterHost && priceFilter) {
      const mobileFiltersTemplate = cloneTemplate('[data-ppa-search-mobile-filters-template]');
      const mobileFilters = mobileFiltersTemplate?.querySelector('.ppa-boost-search__mobile-filters');
      const mobileFiltersContent = mobileFilters?.querySelector('[data-ppa-mobile-filters-content]');

      if (mobileFilters && mobileFiltersContent) {
        mobileFiltersContent.append(priceFilter);
        refinements.forEach((refine) => mobileFiltersContent.append(refine));
        mobileFilterHost.prepend(mobileFilters);

        if (mobileResultsBlock?.parentElement === mobileFilterHost) {
          mobileFilters.after(mobileResultsBlock);
        }
      } else {
        column.prepend(priceFilter);
        refinements.forEach((refine) => column.append(refine));
      }
    } else if (priceFilter) {
      column.prepend(priceFilter);
    }

    if (!isMobile && refinements.length) {
      refinements.forEach((refine) => column.append(refine));
    }
  };

  const activeCurrency = () => window.Shopify?.currency?.active || 'AUD';

  const formatPrice = (value) => {
    const documentLocale = document.documentElement.lang || 'en-AU';
    const locale = documentLocale.toLowerCase().startsWith('en') ? 'en-AU' : documentLocale;
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: activeCurrency(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const serialisePrice = (value) =>
    Number(value)
      .toFixed(2)
      .replace(/\.00$/, '')
      .replace(/(\.\d)0$/, '$1');

  const fetchProductPriceRange = (href) => {
    const url = new URL(href, window.location.origin);
    const key = url.pathname.replace(/\/$/, '');
    if (productPriceCache.has(key)) return productPriceCache.get(key);

    let productRequest = b2bProductRequests ? window.PpaB2BPricing.product(href) : null;
    if (!productRequest) {
      productRequest = window.fetch(`${key}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }).then((response) => {
        if (!response.ok) throw new Error(`Product request failed with ${response.status}`);
        return response.json();
      }).catch((error) => {
        b2bProductRequests?.delete(key);
        throw error;
      });
      b2bProductRequests?.set(key, productRequest);
    }
    const request = productRequest
      .then((product) => {
        const prices = (product.variants || [])
          .map((variant) => Number(variant.price))
          .filter((price) => Number.isFinite(price));
        if (!prices.length) return null;
        const displayedPrice = Math.min(...prices) / 100;
        return {
          minimum: displayedPrice,
          maximum: displayedPrice,
          productType: String(product.type || '').trim(),
        };
      })
      .catch(() => {
        productPriceCache.delete(key);
        return null;
      });

    productPriceCache.set(key, request);
    return request;
  };

  const updatePriceSliderSelection = (wrapper, slider, source) => {
    const minimumInput = slider.querySelector('[data-ppa-price-range-min]');
    const maximumInput = slider.querySelector('[data-ppa-price-range-max]');
    if (!minimumInput || !maximumInput) return;

    let selectedMinimum = Number(minimumInput.value);
    let selectedMaximum = Number(maximumInput.value);

    if (selectedMinimum > selectedMaximum) {
      if (source === minimumInput) {
        selectedMaximum = selectedMinimum;
        maximumInput.value = String(selectedMaximum);
      } else {
        selectedMinimum = selectedMaximum;
        minimumInput.value = String(selectedMinimum);
      }
    }

    const availableMinimum = Number(minimumInput.min);
    const availableMaximum = Number(maximumInput.max);
    const spread = availableMaximum - availableMinimum;
    const from = spread > 0 ? ((selectedMinimum - availableMinimum) / spread) * 100 : 0;
    const to = spread > 0 ? ((selectedMaximum - availableMinimum) / spread) * 100 : 100;

    slider.style.setProperty('--ppa-price-from', `${Math.max(0, Math.min(100, from))}%`);
    slider.style.setProperty('--ppa-price-to', `${Math.max(0, Math.min(100, to))}%`);
    minimumInput.setAttribute('aria-valuetext', formatPrice(selectedMinimum));
    maximumInput.setAttribute('aria-valuetext', formatPrice(selectedMaximum));

    const selection = slider.querySelector('[data-ppa-price-selection]');
    if (selection) {
      selection.textContent =
        selectedMinimum === selectedMaximum
          ? formatPrice(selectedMinimum)
          : `${formatPrice(selectedMinimum)} to ${formatPrice(selectedMaximum)}`;
    }

    const selectedProductTypes = new Set(
      Array.from(wrapper.querySelectorAll('[data-ppa-product-type-input]:checked')).map((input) => input.value)
    );
    const selectedAvailability = new Set(
      Array.from(wrapper.querySelectorAll('[data-ppa-availability-input]:checked')).map((input) => input.value)
    );

    wrapper.querySelectorAll(selectors.product).forEach((card) => {
      const productMinimum = Number(card.dataset.ppaPriceMinimum);
      const productMaximum = Number(card.dataset.ppaPriceMaximum);
      if (!Number.isFinite(productMinimum) || !Number.isFinite(productMaximum)) return;
      const priceIsOutsideRange = productMaximum < selectedMinimum || productMinimum > selectedMaximum;
      const typeIsExcluded = selectedProductTypes.size > 0 && !selectedProductTypes.has(card.dataset.ppaProductType || '');
      const availabilityIsExcluded =
        selectedAvailability.size > 0 && !selectedAvailability.has(card.dataset.ppaAvailability || '');
      card.hidden = priceIsOutsideRange || typeIsExcluded || availabilityIsExcluded;
    });

    const viewAll = wrapper.querySelector(selectors.viewAll);
    const query = wrapper.dataset.ppaSearchQuery || '';
    if (viewAll && query) {
      const url = new URL(viewAll.getAttribute('href') || '/search', window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('pf_p_price', `${serialisePrice(selectedMinimum)}:${serialisePrice(selectedMaximum)}`);
      url.searchParams.delete('filter.p.product_type');
      selectedProductTypes.forEach((productType) => url.searchParams.append('filter.p.product_type', productType));
      url.searchParams.delete('filter.v.availability');
      selectedAvailability.forEach((availability) => {
        url.searchParams.append('filter.v.availability', availability === 'In stock' ? '1' : '0');
      });
      viewAll.href = `${url.pathname}${url.search}`;
    }
    updateInstantBusinessPriceState(wrapper);
  };

  const syncProductTypeFilter = (wrapper, slider, products) => {
    const filter = wrapper.querySelector('.ppa-boost-search__product-types');
    const list = filter?.querySelector('[data-ppa-product-type-list]');
    if (!filter || !list) return;

    const productTypes = Array.from(
      new Set(products.map(({ card }) => card.dataset.ppaProductType).filter(Boolean))
    ).sort((first, second) => first.localeCompare(second, 'en-AU'));
    const signature = productTypes.join('|');

    filter.hidden = productTypes.length === 0;
    if (!productTypes.length || list.dataset.ppaProductTypeSignature === signature) return;

    const selectedTypes = new Set(
      Array.from(list.querySelectorAll('[data-ppa-product-type-input]:checked')).map((input) => input.value)
    );
    list.dataset.ppaProductTypeSignature = signature;
    list.replaceChildren();

    productTypes.forEach((productType) => {
      const item = document.createElement('li');
      const label = document.createElement('label');
      const input = document.createElement('input');
      const text = document.createElement('span');
      const count = document.createElement('span');
      const matchingProducts = products.filter(({ card }) => card.dataset.ppaProductType === productType).length;

      input.type = 'checkbox';
      input.value = productType;
      input.dataset.ppaProductTypeInput = '';
      input.checked = selectedTypes.has(productType);
      input.addEventListener('change', () => updatePriceSliderSelection(wrapper, slider, input));
      text.textContent = productType;
      count.textContent = `(${matchingProducts})`;
      count.className = 'ppa-boost-search__product-type-count';
      label.append(input, text, count);
      item.append(label);
      list.append(item);
    });
  };

  const inferAvailability = (card) => {
    const text = card.textContent || '';
    if (/\b(out of stock|sold out|unavailable)\b/i.test(text)) return 'Out of stock';
    if (/\bin stock\b/i.test(text)) return 'In stock';
    return '';
  };

  const syncAvailabilityFilter = (wrapper, slider, products) => {
    const filter = wrapper.querySelector('.ppa-boost-search__availability');
    const list = filter?.querySelector('[data-ppa-availability-list]');
    if (!filter || !list) return;

    products.forEach(({ card }) => {
      card.dataset.ppaAvailability = inferAvailability(card);
    });

    const availabilityOptions = ['In stock', 'Out of stock'].filter((availability) =>
      products.some(({ card }) => card.dataset.ppaAvailability === availability)
    );
    const signature = availabilityOptions.join('|');

    filter.hidden = availabilityOptions.length === 0;
    if (!availabilityOptions.length || list.dataset.ppaAvailabilitySignature === signature) return;

    const selectedOptions = new Set(
      Array.from(list.querySelectorAll('[data-ppa-availability-input]:checked')).map((input) => input.value)
    );
    list.dataset.ppaAvailabilitySignature = signature;
    list.replaceChildren();

    availabilityOptions.forEach((availability) => {
      const item = document.createElement('li');
      const label = document.createElement('label');
      const input = document.createElement('input');
      const text = document.createElement('span');
      const count = document.createElement('span');
      const matchingProducts = products.filter(
        ({ card }) => card.dataset.ppaAvailability === availability
      ).length;

      input.type = 'checkbox';
      input.value = availability;
      input.dataset.ppaAvailabilityInput = '';
      input.checked = selectedOptions.has(availability);
      input.addEventListener('change', () => updatePriceSliderSelection(wrapper, slider, input));
      text.textContent = availability;
      count.textContent = `(${matchingProducts})`;
      count.className = 'ppa-boost-search__product-type-count';
      label.append(input, text, count);
      item.append(label);
      list.append(item);
    });
  };

  const syncPriceSlider = async (wrapper, query) => {
    const slider = wrapper.querySelector('[data-ppa-price-slider]');
    const filter = slider?.closest('.ppa-boost-search__price-filter');
    if (!slider || !filter) return;

    const products = Array.from(wrapper.querySelectorAll(selectors.product))
      .map((card) => ({ card, link: card.querySelector('a[href*="/products/"]') }))
      .filter(({ link }) => link);
    const signature = `${query}|${products.map(({ link }) => link.pathname || link.getAttribute('href')).join('|')}`;
    const productsHavePrices = products.every(
      ({ card }) => card.dataset.ppaPriceMinimum && card.dataset.ppaPriceMaximum
    );
    if (slider.dataset.ppaPriceSignature === signature && productsHavePrices) return;

    slider.dataset.ppaPriceSignature = signature;
    slider.hidden = true;
    filter.hidden = false;

    const ranges = await Promise.all(
      products.map(async ({ card, link }) => {
        const range = await fetchProductPriceRange(link.href);
        if (range) {
          card.dataset.ppaPriceMinimum = String(range.minimum);
          card.dataset.ppaPriceMaximum = String(range.maximum);
          card.dataset.ppaProductType = range.productType;
        }
        return range;
      })
    );

    if (!slider.isConnected || slider.dataset.ppaPriceSignature !== signature) return;

    const validRanges = ranges.filter(Boolean);
    if (!validRanges.length) {
      filter.hidden = true;
      return;
    }

    const availableMinimum = Math.min(...validRanges.map((range) => range.minimum));
    const availableMaximum = Math.max(...validRanges.map((range) => range.maximum));
    const minimumInput = slider.querySelector('[data-ppa-price-range-min]');
    const maximumInput = slider.querySelector('[data-ppa-price-range-max]');
    const ticks = Array.from(slider.querySelectorAll('[data-ppa-price-ticks] span'));
    if (!minimumInput || !maximumInput) return;

    [minimumInput, maximumInput].forEach((input) => {
      input.min = String(availableMinimum);
      input.max = String(availableMaximum);
      input.step = '0.01';
    });
    minimumInput.value = String(availableMinimum);
    maximumInput.value = String(availableMaximum);

    const spread = availableMaximum - availableMinimum;
    slider.dataset.ppaFixedPrice = spread === 0 ? 'true' : 'false';
    minimumInput.disabled = spread === 0;
    maximumInput.disabled = spread === 0;
    ticks.forEach((tick, index) => {
      tick.textContent = formatPrice(availableMinimum + spread * (index / Math.max(1, ticks.length - 1)));
    });

    if (slider.dataset.ppaPriceBound !== 'true') {
      slider.dataset.ppaPriceBound = 'true';
      slider.addEventListener('input', (event) => updatePriceSliderSelection(wrapper, slider, event.target));
    }

    slider.hidden = false;
    syncProductTypeFilter(wrapper, slider, products);
    syncAvailabilityFilter(wrapper, slider, products);
    updatePriceSliderSelection(wrapper, slider);
  };

  const showVerifiedBestSellers = (wrapper, state) => {
    const shouldReplaceZeroState = state === 'zero';
    const hasBoostProducts = Array.from(wrapper.querySelectorAll(selectors.product)).some(
      (product) => !product.closest(selectors.zeroProductList)
    );
    const shouldShowRecovery =
      state === 'typed' && !hasBoostProducts && wrapper.dataset.ppaSearchContent !== 'editorial';
    const desiredState = shouldReplaceZeroState ? 'zero' : shouldShowRecovery ? 'recovery' : null;

    wrapper.querySelectorAll(selectors.zeroProductList).forEach((list) => {
      list.hidden = shouldReplaceZeroState;
    });

    const existing = wrapper.querySelector('.ppa-boost-search__best-sellers');
    if (!desiredState) {
      existing?.remove();
      wrapper.querySelectorAll('[data-ppa-created-product-column]').forEach((column) => column.remove());
      return;
    }

    if (existing?.dataset.ppaBestSellersState === desiredState) return;
    existing?.remove();

    const autocomplete = wrapper.querySelector(selectors.autocomplete);
    if (!autocomplete) return;

    let column = autocomplete.querySelector(selectors.productColumn);
    if (!column) {
      column = document.createElement('section');
      column.className = 'boost-sd__instant-search-column boost-sd__instant-search-column--products ppa-boost-search__recovery-column';
      column.dataset.ppaCreatedProductColumn = '';
      autocomplete.append(column);
    }

    const frequentTemplate = document.querySelector('[data-ppa-search-frequent-products-template]');
    const content = cloneTemplate(
      frequentTemplate
        ? '[data-ppa-search-frequent-products-template]'
        : '[data-ppa-search-best-sellers-template]'
    );
    if (!content) return;
    column.append(content);
    const inserted = column.querySelector('.ppa-boost-search__best-sellers');
    if (!inserted) return;
    if (frequentTemplate && !renderFrequentlyOrderedProducts(inserted)) {
      inserted.remove();
      wrapper.querySelectorAll(selectors.zeroProductList).forEach((list) => {
        list.hidden = false;
      });
      if (column.hasAttribute('data-ppa-created-product-column')) column.remove();
      return;
    }
    inserted.dataset.ppaBestSellersState = desiredState;
  };

  const productMetadata = async (productUrl, fallbackSku = '') => {
    let url;
    try {
      url = new URL(productUrl, window.location.origin);
    } catch (_error) {
      return {
        parentSku: fallbackSku,
        trustpilotSkuGroup: '',
        hasBusinessPrice: null,
        businessPriceLabel: '',
      };
    }

    const handleMatch = url.pathname.match(/\/products\/([^/?#]+)/);
    if (!handleMatch || url.origin !== window.location.origin) {
      return {
        parentSku: fallbackSku,
        trustpilotSkuGroup: '',
        hasBusinessPrice: null,
        businessPriceLabel: '',
      };
    }

    const handle = decodeURIComponent(handleMatch[1]);
    const fallback = {
      parentSku: fallbackSku,
      trustpilotSkuGroup: '',
      hasBusinessPrice: null,
      businessPriceLabel: '',
    };

    const metadataStorageKey = '';
    if (!productMetadataCache.has(handle) && metadataStorageKey) {
      try {
        const cached = JSON.parse(window.sessionStorage.getItem(metadataStorageKey) || 'null');
        if (cached && Date.now() >= cached.time && Date.now() - cached.time < 5 * 60 * 1000 &&
            typeof cached.data?.hasBusinessPrice === 'boolean' && cached.data.trustpilotSkuGroup) {
          productMetadataCache.set(handle, Promise.resolve(cached.data));
        }
      } catch (_error) {}
    }
    if (!productMetadataCache.has(handle)) {
      const productMetadataUrl = new URL(url.href);
      productMetadataUrl.search = '';
      productMetadataUrl.searchParams.set('view', 'ppa-card-data');
      productMetadataUrl.hash = '';

      const lookup = async () => {
        if (b2bMetadataKey) {
          const product = await window.PpaB2BPricing.product(productUrl);
          return { parentSku: product.parentSku, trustpilotSkuGroup: product.trustpilotSkuGroup, hasBusinessPrice: product.hasBusinessPrice, businessPriceLabel: product.businessPriceLabel, product };
        }
        const response = await fetch(productMetadataUrl.href, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Product lookup failed with ${response.status}`);
        const metadata = await response.json();
        if (typeof metadata.hasBusinessPrice !== 'boolean') throw new Error('Invalid product pricing metadata');
        return {
          parentSku: metadata.parentSku || fallback.parentSku,
          trustpilotSkuGroup: metadata.trustpilotSkuGroup || fallback.trustpilotSkuGroup,
          hasBusinessPrice: metadata.hasBusinessPrice,
          businessPriceLabel: metadata.businessPriceLabel || '',
        };
      };
      const request = lookup().catch(lookup).then((data) => {
        if (metadataStorageKey && data.trustpilotSkuGroup) {
          try {
            window.sessionStorage.setItem(metadataStorageKey, JSON.stringify({ time: Date.now(), data }));
          } catch (_error) {}
        }
        return data;
      }).catch(() => {
        productMetadataCache.delete(handle);
        return fallback;
      });

      productMetadataCache.set(handle, request);
    }

    return productMetadataCache.get(handle);
  };

  const syncBusinessPriceLabel = (product, skuElement, metadata) => {
    if (b2bMetadataKey && metadata.product) {
      window.PpaB2BBoostPricing?.apply(product, metadata.product);
      applyInstantBusinessPriceFilter(product.closest(selectors.wrapper) || document);
      applyResultsBusinessPriceFilter();
      return;
    }
    if (b2bMetadataKey && !metadata.product) {
      product.querySelectorAll('.ppa-b2b-business-price-label--injected, .boost-sd__suggestion-queries-item-badge--sale, .boost-sd__product-label--sale').forEach((label) => label.remove());
    }
    const hasBusinessPrice = metadata.hasBusinessPrice === true;
    product.dataset.ppaBusinessPrice = typeof metadata.hasBusinessPrice === 'boolean'
      ? String(metadata.hasBusinessPrice)
      : 'error';

    const existingLabel = product.querySelector(
      '.ppa-b2b-business-price-label, .boost-sd__suggestion-queries-item-badge--sale, .boost-sd__product-label--sale'
    );

    if (!hasBusinessPrice) {
      product.querySelector('.ppa-b2b-business-price-label--injected')?.remove();
      applyInstantBusinessPriceFilter(product.closest(selectors.wrapper) || document);
      applyResultsBusinessPriceFilter();
      return;
    }

    if (existingLabel) {
      existingLabel.classList.add('ppa-b2b-business-price-label');
    } else if (skuElement) {
      const label = document.createElement('span');
      label.className = 'ppa-b2b-business-price-label ppa-b2b-business-price-label--injected';
      const labelText = metadata.businessPriceLabel || b2bSearchText().businessPrice || '';
      label.textContent = labelText;
      label.setAttribute('aria-label', labelText);
      skuElement.insertAdjacentElement('afterend', label);
    }

    applyInstantBusinessPriceFilter(product.closest(selectors.wrapper) || document);
    applyResultsBusinessPriceFilter();
  };

  const applyParentSku = async (product, skuElement, productUrl, fallbackSku) => {
    if (!productUrl || product.dataset.ppaParentSkuApplied) return;
    product.dataset.ppaParentSkuApplied = 'loading';

    const metadata = await productMetadata(productUrl, fallbackSku);
    if (!product.isConnected) return;
    if (metadata.parentSku && skuElement) skuElement.textContent = `SKU: ${metadata.parentSku}`;
    syncBusinessPriceLabel(product, skuElement, metadata);
    product.dataset.ppaParentSkuApplied = 'true';
  };

  const trustpilotSkuGroup = async (productUrl, fallbackSku = '') => {
    const metadata = await productMetadata(productUrl, fallbackSku);
    return metadata.trustpilotSkuGroup;
  };

  const initialiseTrustpilotWidget = async (widget) => {
    if (widget.dataset.ppaTrustpilotInitialised) return;
    widget.dataset.ppaTrustpilotInitialised = 'loading';

    const skuGroup = widget.dataset.ppaTrustpilotSkus || await trustpilotSkuGroup(
      widget.dataset.ppaProductUrl,
      widget.dataset.ppaTrustpilotFallbackSku || widget.dataset.sku || ''
    );
    if (!widget.isConnected) return;
    if (!skuGroup) {
      // A failed lookup must not present a single pack's reviews as all product reviews.
      widget.hidden = true;
      delete widget.dataset.ppaTrustpilotInitialised;
      return;
    }
    widget.dataset.ppaTrustpilotSkus = skuGroup;
    widget.dataset.sku = skuGroup;
    widget.hidden = false;

    if (!window.ppaTrustpilotReviews) {
      delete widget.dataset.ppaTrustpilotInitialised;
      const attempts = Number(widget.dataset.ppaTrustpilotAttempts || 0);
      if (attempts >= 20) return;
      widget.dataset.ppaTrustpilotAttempts = String(attempts + 1);
      window.setTimeout(() => initialiseTrustpilotWidget(widget), 250);
      return;
    }

    window.ppaTrustpilotReviews.initialise(widget);
    delete widget.dataset.ppaTrustpilotAttempts;
    widget.dataset.ppaTrustpilotInitialised = 'true';
  };

  const initialiseTrustpilotWidgetsWhenIdle = (root) => {
    if (root.dataset.ppaTrustpilotQueued === 'true') return;
    root.dataset.ppaTrustpilotQueued = 'true';

    const initialise = () => {
      delete root.dataset.ppaTrustpilotQueued;
      if (!root.isConnected) return;
      root.querySelectorAll('.ppa-boost-search__trustpilot').forEach(initialiseTrustpilotWidget);
      makeTrustpilotDisplayOnly(root);
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(initialise, { timeout: 900 });
    } else {
      window.setTimeout(initialise, 0);
    }
  };

  const makeTrustpilotDisplayOnly = (root = document) => {
    root
      .querySelectorAll('.ppa-boost-search__trustpilot, .ppa-results-card__trustpilot')
      .forEach((review) => {
        review.dataset.ppaTrustpilotDisplayOnly = 'true';
        review.querySelectorAll('a, iframe').forEach((interactive) => {
          interactive.setAttribute('tabindex', '-1');
          if (interactive.matches('a')) interactive.setAttribute('aria-disabled', 'true');
        });
      });
  };

  const productCardData = (card) => {
    try {
      return JSON.parse(card.getAttribute('data-product') || '{}');
    } catch (_error) {
      return {};
    }
  };

  const hideResultsFilterIcons = () => {
    document.querySelectorAll('.boost-sd__filter-tree-vertical .boost-sd__filter-option-icon').forEach((icon) => {
      icon.style.setProperty('display', 'none', 'important');
    });
  };

  const openDesktopResultsFilters = () => {
    hideResultsFilterIcons();
    const isDesktop = window.matchMedia('(min-width: 990px)').matches;

    document.querySelectorAll('.boost-sd__filter-tree-vertical .boost-sd__filter-option-title').forEach((button) => {
      if (!isDesktop) {
        if (button.dataset.ppaDesktopFilterLocked === 'true') {
          button.removeAttribute('aria-disabled');
          button.removeAttribute('tabindex');
          delete button.dataset.ppaDesktopFilterLocked;
        }
        return;
      }

      const option = button.closest('.boost-sd__filter-option');
      const target =
        (button.id ? document.getElementById(`${button.id}-target`) : null) ||
        option?.querySelector('.boost-sd__filter-option-itemwrapper');

      if (!target) {
        button.removeAttribute('aria-disabled');
        button.removeAttribute('tabindex');
        delete button.dataset.ppaDesktopFilterLocked;
        return;
      }

      button.setAttribute('aria-expanded', 'true');
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('tabindex', '-1');
      button.dataset.ppaDesktopFilterLocked = 'true';
      button.closest('.boost-sd__filter-option-label')?.classList.remove('boost-sd__filter-option-label--collapsed');
      target.classList.remove('boost-sd__filter-option-itemwrapper--collapsed');
      target.removeAttribute('hidden');
      target.removeAttribute('aria-hidden');
    });
  };

  const ensureCollectionTitle = () => {
    const source = document.querySelector('[data-ppa-collection-title]');
    const existing = document.querySelector('[data-ppa-results-collection-heading]');
    const nativeTitle = Array.from(document.querySelectorAll('.collection-hero__title')).find(
      (title) => !title.closest('[data-ppa-results-collection-heading]')
    );

    if (!source || nativeTitle) {
      existing?.remove();
      return;
    }

    const titleText = source.dataset.ppaCollectionTitle?.trim();
    const results = document.querySelector('.boost-sd__filter-product-list');
    const layout = results?.querySelector('.boost-sd-layout');
    if (!titleText || !layout?.parentElement) return;

    let heading = existing;
    if (!heading) {
      heading = document.createElement('section');
      heading.className = 'collection-hero ppa-results-collection-heading';
      heading.dataset.ppaResultsCollectionHeading = '';
      heading.setAttribute('aria-labelledby', 'ppa-results-collection-title');

      const inner = document.createElement('div');
      inner.className = 'collection-hero__inner page-width';

      const textWrapper = document.createElement('div');
      textWrapper.className = 'collection-hero__text-wrapper';

      const title = document.createElement('h1');
      title.className = 'collection-hero__title';
      title.id = 'ppa-results-collection-title';
      title.textContent = titleText;

      const actions = document.createElement('div');
      actions.className = 'ppa-results-collection-header-actions';
      actions.setAttribute('aria-label', 'Collection controls');

      textWrapper.append(title);
      const searchTip = source.dataset.ppaCollectionSearchTip?.trim();
      if (searchTip) {
        const hint = document.createElement('p');
        hint.className = 'reseller-catalogue-search-tip';
        hint.textContent = searchTip;
        textWrapper.append(hint);
      }
      inner.append(textWrapper, actions);
      heading.append(inner);
    }

    if (heading.nextElementSibling !== layout) layout.before(heading);
  };

  const placeResultsToolbar = () => {
    const slot = document.querySelector('.ppa-results-collection-header-actions');
    const results = document.querySelector('.boost-sd__filter-product-list');
    const layout = results?.querySelector('.boost-sd-layout');
    const resultsContainer = layout?.parentElement;
    const toolbar = results?.querySelector('.boost-sd__toolbar-container');
  const toolbarInner = document.querySelector(
    '.boost-sd__filter-product-list .boost-sd__toolbar-container .boost-sd__toolbar-inner'
  );
  const currentToolbarContent = toolbarInner?.querySelector('.boost-sd__toolbar-content');
  const slottedToolbarContent = slot?.querySelector('.boost-sd__toolbar-content');
  const toolbarContent = currentToolbarContent?.querySelector('.boost-sd__product-count, .boost-sd__sorting')
    ? currentToolbarContent
    : slottedToolbarContent || currentToolbarContent;
    if (!toolbar || !toolbarInner || !toolbarContent) return;

    const isDesktop = window.matchMedia('(min-width: 990px)').matches;

    if (!slot) {
      const rightColumn = results?.querySelector('.boost-sd-right');

      if (isDesktop && resultsContainer && layout) {
        toolbar.dataset.ppaWideResultsToolbar = 'true';
        if (toolbar.parentElement !== resultsContainer) resultsContainer.insertBefore(toolbar, layout);
      } else if (!isDesktop && rightColumn && toolbar.parentElement !== rightColumn) {
        delete toolbar.dataset.ppaWideResultsToolbar;
        rightColumn.prepend(toolbar);
      }
      return;
    }

    if (!isDesktop) {
      if (toolbarContent.parentElement !== toolbarInner) toolbarInner.append(toolbarContent);
      return;
    }

    if (toolbarContent.parentElement === slot) return;

    slot.querySelector('.boost-sd__toolbar-content')?.remove();
    slot.append(toolbarContent);
  };

  const normaliseResultsSortControl = () => {
    document.querySelectorAll('.boost-sd__sorting-button').forEach((button) => {
      const value = button.querySelector('.boost-sd__sorting-value');
      if (!value) return;

      let label = button.querySelector('.ppa-results-sort-label');
      if (!label) {
        Array.from(button.childNodes).forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE && /sort\s*by\s*:/i.test(node.textContent || '')) {
            node.textContent = '';
          }
        });

        label = document.createElement('span');
        label.className = 'ppa-results-sort-label';
        label.textContent = 'Sort By:';
        button.insertBefore(label, value);
      }

      if (value.textContent.trim()) return;

      const fallback = document.querySelector('.boost-sd__sorting-option-item')?.textContent?.trim() || 'Relevance';
      value.textContent = fallback;
    });
  };

  const syncSelectedFilters = () => {
    const toolbar = document.querySelector('.boost-sd__filter-product-list .boost-sd__toolbar-container');
    const host = toolbar?.querySelector('.boost-sd__selected-filter-values');
    const source = Array.from(document.querySelectorAll('.boost-sd__refine-by-vertical-refine-by')).find(
      (element) => !element.closest('.boost-sd__toolbar-container')
    );
    if (!host) return;

    const sourceItems = Array.from(
      source?.querySelectorAll('.boost-sd__refine-by-vertical-refine-by-item') || []
    );
    const signature = sourceItems.map((item) => item.getAttribute('aria-label') || item.textContent.trim()).join('|');
    if (host.dataset.ppaSelectedFilterSignature === signature) return;

    host.dataset.ppaSelectedFilterSignature = signature;
    host.replaceChildren();
    if (!sourceItems.length) return;

    const list = document.createElement('div');
    list.className = 'boost-sd__refine-by-vertical-refine-by-list';
    sourceItems.forEach((sourceItem) => {
      const chip = sourceItem.cloneNode(true);
      chip.removeAttribute('id');
      chip.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      chip.addEventListener('click', () => sourceItem.click());
      list.append(chip);
    });
    host.append(list);
  };

  const enhanceResultsCards = () => {
    normaliseSaleBadges();
    ensureResultsBusinessPriceFilter();
    applyResultsBusinessPriceFilter();

    const productList = document.querySelector('.boost-sd__filter-product-list .boost-sd__product-list');
    if (productList) {
      const visibleProducts = Array.from(productList.children).filter(
        (child) =>
          child.matches('.boost-sd__product-item') &&
          !child.hidden &&
          child.getAttribute('aria-hidden') !== 'true' &&
          !child.classList.contains('ppa-business-price-filtered')
      );
      const visibleProductCount = visibleProducts.length > 4 ? 'many' : String(visibleProducts.length);

      if (productList.dataset.ppaVisibleProducts !== visibleProductCount) {
        productList.dataset.ppaVisibleProducts = visibleProductCount;
      }
    }

    document.querySelectorAll('.boost-sd__filter-product-list .boost-sd__product-item').forEach((card) => {
      const product = productCardData(card);
      const price = card.querySelector('.boost-sd__product-price');
      const info = price?.parentElement || card.querySelector('.boost-sd__product-info');
      if (!info || !price) return;
      const skuElement = card.querySelector('.sku-title');
      const skuText = skuElement?.textContent?.replace(/^sku\s*:\s*/i, '').trim() || '';
      const productUrl = card.querySelector('a[href*="/products/"]')?.href || '';
      applyParentSku(card, skuElement, productUrl, skuText);

      let review = card.querySelector('.ppa-results-card__trustpilot');
      if (!review) {
        const existingWidget = card.querySelector('.trustpilot-prod-mini .trustpilot-widget, .trustpilot-widget');
        const reviewSku = existingWidget?.dataset.sku || skuText.replace(/\([^)]*\)$/, '') || product.sku || '';
        const wrapper = existingWidget?.closest('.trustpilot-prod-mini') || document.createElement('div');

        wrapper.className = 'ppa-results-card__trustpilot';
        wrapper.replaceChildren();

        const widget = document.createElement('div');
        widget.className = 'trustpilot-widget';
        widget.dataset.locale = 'en-GB';
        widget.dataset.templateId = '577258fb31f02306e4e3aaf9';
        widget.dataset.businessunitId = '64e718d7979bae87c95b30a9';
        widget.dataset.styleHeight = '24px';
        widget.dataset.styleWidth = '100%';
        widget.dataset.token = '4ddc2d1a-c2f5-470e-95cb-be859565b75a';
        widget.dataset.ppaTrustpilotFallbackSku = reviewSku;
        widget.dataset.ppaProductUrl = productUrl;
        widget.dataset.starColor = '#d76800';
        widget.dataset.noReviews = 'show';
        widget.dataset.scrollToList = 'false';
        widget.dataset.styleAlignment = 'left';

        const link = document.createElement('a');
        link.href = 'https://uk.trustpilot.com/review/pacificpackaging.com.au';
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = 'Trustpilot';
        widget.append(link);
        wrapper.append(widget);

        if (!existingWidget) info.insertBefore(wrapper, price);
        review = wrapper;
        initialiseTrustpilotWidget(widget);
      }

      let socialProof = card.querySelector('.ppa-results-card__social-proof');
      if (!socialProof) {
        socialProof = document.createElement('div');
        socialProof.className = 'ppa-results-card__social-proof';
        price.before(socialProof);
      }

      if (review && review.parentElement !== socialProof) socialProof.append(review);

      card.querySelectorAll('.trustpilot-widget').forEach((widget) => {
        if (widget.closest('.ppa-results-card__trustpilot')) return;
        const legacyWrapper = widget.closest('.trustpilot-prod-mini');
        (legacyWrapper || widget).remove();
      });

      card.querySelectorAll('.ppa-results-card__packpoints, [data-packpoints], [data-pack-points]').forEach((element) => {
        element.remove();
      });
      makeTrustpilotDisplayOnly(card);
    });

    openDesktopResultsFilters();
    ensureCollectionTitle();
    placeResultsToolbar();
    normaliseResultsSortControl();
    syncSelectedFilters();
  };

  const queueResultsEnhancement = () => {
    if (!b2bMetadataKey) {
      window.requestAnimationFrame(enhanceResultsCards);
      return;
    }
    if (resultsFrame) return;
    resultsFrame = window.requestAnimationFrame(() => {
      resultsFrame = null;
      enhanceResultsCards();
    });
  };

  const observeResultsCards = () => {
    hideResultsFilterIcons();
    const results = document.querySelector('.boost-sd__filter-product-list');
    if (!results) return false;

    if (results === observedResultsRoot) return true;

    resultsObserver?.disconnect();
    observedResultsRoot = results;
    resultsObserver = new MutationObserver((records) => {
      const hasExternalChange = records.some((record) => {
        const target = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
        return !target?.closest?.('[data-ppa-card-commerce], .ppa-results-card__trustpilot');
      });

      if (hasExternalChange) queueResultsEnhancement();
    });
    resultsObserver.observe(results, { childList: true, subtree: true });
    enhanceResultsCards();
    return true;
  };

  const addProductReviews = (wrapper) => {
    wrapper.querySelectorAll(selectors.product).forEach((product) => {
      if (product.closest(selectors.zeroProductList)?.hidden) return;
      if (product.querySelector(':scope > .ppa-boost-search__trustpilot')) return;

      const skuElement = product.querySelector(selectors.sku);
      const sku = skuElement?.textContent?.replace(/^sku\s*:\s*/i, '').trim();
      if (!sku) return;
      const productUrl = product.querySelector('a[href*="/products/"]')?.href || '';
      applyParentSku(product, skuElement, productUrl, sku);

      const widget = document.createElement('div');
      widget.className = 'trustpilot-widget ppa-boost-search__trustpilot';
      widget.dataset.locale = 'en-GB';
      widget.dataset.templateId = '577258fb31f02306e4e3aaf9';
      widget.dataset.businessunitId = '64e718d7979bae87c95b30a9';
      widget.dataset.styleHeight = '24px';
      widget.dataset.styleWidth = '100%';
      widget.dataset.token = '6f2ebc69-550c-4f27-b5d7-7cad6cfbedf1';
      widget.dataset.ppaTrustpilotFallbackSku = sku;
      widget.dataset.ppaProductUrl = productUrl;
      widget.dataset.starColor = '#d76800';
      widget.dataset.noReviews = 'show';
      widget.dataset.scrollToList = 'false';
      widget.dataset.styleAlignment = 'left';

      const link = document.createElement('a');
      link.href = 'https://uk.trustpilot.com/review/pacificpackaging.com.au';
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = 'Trustpilot';
      widget.append(link);
      product.append(widget);
    });

    initialiseTrustpilotWidgetsWhenIdle(wrapper);
  };

  const markExactSku = (wrapper, query) => {
    const normalisedQuery = normaliseSku(query);

    wrapper.querySelectorAll(selectors.product).forEach((product) => {
      product.classList.remove('ppa-boost-search__exact-sku');
      if (!normalisedQuery) return;

      const sku = normaliseSku(product.querySelector(selectors.sku)?.textContent || '');
      if (sku === normalisedQuery || sku.startsWith(`${normalisedQuery}(`)) {
        product.classList.add('ppa-boost-search__exact-sku');
      }
    });
  };

  const updateSearchPanelTop = (input, state) => {
    if (!input) {
      document.documentElement.style.removeProperty('--ppa-boost-panel-top');
      return;
    }

    const rect = input.getBoundingClientRect();
    if (rect.height > 0 && rect.bottom > 0) {
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      const gutter = isMobile ? (state === 'ready' ? 8 : 0) : 12;
      document.documentElement.style.setProperty('--ppa-boost-panel-top', `${Math.round(rect.bottom + gutter)}px`);
    }
  };

  function updateSearchInterface() {
    const wrapper = searchWrapper();
    const input = activeInput();
    const query = input?.value?.trim() || '';
    const isMobile = window.matchMedia('(max-width: 989px)').matches;

    if (searchDismissed) {
      wrapper?.classList.add('boost-sd__g-hide');
      document.body.classList.remove(
        'boost-sd__search-opening',
        'ppa-boost-search-open',
        'ppa-boost-search-keyboard-dismissed'
      );
      document.body.removeAttribute('data-ppa-boost-search-state');
      setPageScrollLocked(false);
      document.documentElement.style.removeProperty('--ppa-boost-panel-top');
      input?.setAttribute('aria-expanded', 'false');
      return;
    }

    const searchHasFocus = input?.closest('[data-ppa-boost-search]')?.matches(':focus-within');
    const boostHasOpened = Boolean(
      wrapper &&
        (!wrapper.classList.contains('boost-sd__g-hide') ||
          wrapper.getClientRects().length ||
          input?.getAttribute('aria-expanded') === 'true' ||
          document.body.classList.contains('boost-sd__search-opening'))
    );
    const showReadyMenu = Boolean(wrapper && input && !query && searchMenuActive);
    const showTypedResults = Boolean(
      wrapper &&
        input &&
        query &&
        searchMenuActive &&
        (boostHasOpened || searchOverlayActive) &&
        (searchHasFocus || searchOverlayActive)
    );
    const isOpen = showReadyMenu || showTypedResults;

    if (!isOpen) {
      setPageScrollLocked(false);
      document.body.removeAttribute('data-ppa-boost-search-state');
      if (!query && !searchMenuActive) {
        wrapper?.classList.add('boost-sd__g-hide');
        input?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('boost-sd__search-opening');
      }
      if (!searchOverlayActive || !query) {
        document.body.classList.remove('ppa-boost-search-open', 'ppa-boost-search-keyboard-dismissed');
        document.documentElement.style.removeProperty('--ppa-boost-panel-top');
      }
      return;
    }

    const state = showTypedResults ? 'typed' : 'ready';
    const shouldResetScroll = resetOnOpen || wrapper.dataset.ppaSearchQuery !== query;

    setPageScrollLocked(true);
    wrapper.classList.remove('boost-sd__g-hide');
    wrapper.dataset.ppaSearchPanel = '';
    wrapper.dataset.ppaSearchState = state;
    wrapper.dataset.ppaSearchQuery = query;
    input.setAttribute('aria-expanded', 'true');
    input.setAttribute('placeholder', searchPlaceholder());
    document.body.dataset.ppaBoostSearchState = state;
    updateSearchPanelTop(input, state);
    ensurePanelClose(wrapper);
    markExactSku(wrapper, query);
    updateContentPriority(wrapper);
    showB2BQuickLinks(wrapper, state);
    showVerifiedBestSellers(wrapper, state === 'ready' ? 'zero' : state);
    arrangeSearchColumns(wrapper);
    addRefinementLinks(wrapper, state);
    if (state === 'typed') syncPriceSlider(wrapper, query);
    placeViewAllAtEnd(wrapper);
    normaliseSaleBadges(wrapper);
    addProductReviews(wrapper);
    applyInstantBusinessPriceFilter(wrapper);
    tagResultLinks(wrapper);

    if (state === 'ready') {
      document.body.classList.remove(
        'boost-sd__search-opening',
        'ppa-boost-search-open',
        'ppa-boost-search-keyboard-dismissed'
      );
    } else {
      document.body.classList.add('ppa-boost-search-open');
    }

    if (shouldResetScroll) {
      resetSearchScroll(wrapper);
      window.requestAnimationFrame(() => resetSearchScroll(wrapper));
    }
    resetOnOpen = false;

    const viewAll = wrapper.querySelector(selectors.viewAll);
    if (viewAll && query) viewAll.setAttribute('aria-label', `View all search results for ${query}`);
  }

  const observeBoostContainers = () => {
    enhanceSearchInputs();
    if (isResultsPage) observeResultsCards();
    const containers = document.querySelectorAll(selectors.container);
    if (!containers.length) return false;

    boostObserver?.disconnect();
    boostObserver = new MutationObserver(queueUpdate);
    containers.forEach((container) =>
      boostObserver.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-expanded'],
      })
    );
    discoveryObserver?.disconnect();
    queueUpdate();
    return true;
  };

  const closeMobileSearch = (button) => {
    if (closingSearch) return;
    closingSearch = true;
    const input =
      button.closest('[data-ppa-boost-search]')?.querySelector('input[type="search"]') ||
      activeInput() ||
      document.querySelector(selectors.input);
    mobileSearchActive = false;
    searchMenuActive = false;
    searchOverlayActive = false;
    searchDismissed = true;
    try {
      input?.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', keyCode: 27, bubbles: true }));
    } finally {
      closingSearch = false;
    }

    window.requestAnimationFrame(() => {
      const wrapper = searchWrapper();
      if (wrapper) wrapper.classList.add('boost-sd__g-hide');
      document.body.classList.remove('boost-sd__search-opening');
      document.body.classList.remove('ppa-boost-search-open', 'ppa-boost-search-keyboard-dismissed');
      document.body.removeAttribute('data-ppa-boost-search-state');
      setPageScrollLocked(false);
      document.documentElement.style.removeProperty('--ppa-boost-panel-top');
      input?.setAttribute('aria-expanded', 'false');
      input?.blur();
    });

    window.setTimeout(() => {
      document.body.classList.remove('ppa-boost-search-open', 'ppa-boost-search-keyboard-dismissed');
      setPageScrollLocked(false);
    }, 80);
  };

  document.addEventListener('input', (event) => {
    if (!event.target.matches(selectors.input)) return;
    if (event.isTrusted) searchDismissed = false;
    const hasQuery = Boolean(event.target.value.trim());
    const wasActive = searchOverlayActive;
    trackedInput = event.target;
    searchMenuActive = true;
    searchOverlayActive = hasQuery;
    mobileSearchActive = hasQuery && window.matchMedia('(max-width: 989px)').matches;
    if (hasQuery) {
      document.body.classList.remove('ppa-boost-search-keyboard-dismissed');
      if (!wasActive) resetOnOpen = true;
    }
    syncSearchControl(event.target);
    queueUpdate();
  });

  document.addEventListener('focusin', (event) => {
    if (!event.target.matches(selectors.input)) return;
    const hasQuery = Boolean(event.target.value.trim());
    trackedInput = event.target;
    searchMenuActive = true;
    searchOverlayActive = hasQuery;
    mobileSearchActive = hasQuery && window.matchMedia('(max-width: 989px)').matches;
    if (hasQuery) {
      document.body.classList.remove('ppa-boost-search-keyboard-dismissed');
      resetOnOpen = true;
    } else {
      document.body.classList.remove(
        'boost-sd__search-opening',
        'ppa-boost-search-open',
        'ppa-boost-search-keyboard-dismissed'
      );
      resetOnOpen = true;
    }
    queueUpdate();
    window.setTimeout(queueUpdate, 100);
  });

  document.addEventListener('focusout', (event) => {
    if (!event.target.matches(selectors.input)) return;
    window.setTimeout(() => {
      // Moving focus from the search field into the mobile filter controls is
      // part of the same search interaction. Keep the panel anchored directly
      // below the field instead of creating a second close control and spacer.
      document.body.classList.remove('ppa-boost-search-keyboard-dismissed');
      queueUpdate();
    }, 0);
  });

  document.addEventListener('click', (event) => {
    const clearInstantBusinessPrice = event.target.closest('[data-ppa-clear-instant-business-price]');
    if (clearInstantBusinessPrice) {
      event.preventDefault();
      setInstantBusinessPriceFilter(false);
      const wrapper = clearInstantBusinessPrice.closest(selectors.wrapper);
      if (wrapper) {
        applyInstantBusinessPriceFilter(wrapper);
        const checkbox = wrapper.querySelector('[data-ppa-business-price-only]');
        const focusTarget = window.matchMedia('(max-width: 989px)').matches
          ? wrapper.querySelector('.ppa-boost-search__mobile-filters > summary') || activeInput()
          : checkbox || activeInput();
        focusTarget?.focus({ preventScroll: true });
      }
      return;
    }
    const clearBusinessPrice = event.target.closest('[data-ppa-clear-results-business-price]');
    if (clearBusinessPrice) {
      event.preventDefault();
      setResultsBusinessPriceFilter(false);
      enhanceResultsCards();
      const focusTarget = window.matchMedia('(min-width: 990px)').matches
        ? resultsBusinessPriceCheckbox()
        : document.querySelector('button[aria-label="Open Filter Sidebar"]');
      focusTarget?.focus({ preventScroll: true });
      return;
    }
    const clickedSearchInput = event.target.matches?.(selectors.input) ? event.target : null;
    if (clickedSearchInput) {
      searchDismissed = false;
      trackedInput = clickedSearchInput;
      searchMenuActive = true;
      searchOverlayActive = Boolean(clickedSearchInput.value.trim());
      queueUpdate();

      if (searchOverlayActive) return;

      // Keep the active field focused while Boost builds the ready menu so the
      // text caret remains visible and the next keystroke starts searching.
      const restoreSearchFocus = () => {
        if (!clickedSearchInput.isConnected || !searchMenuActive) return;
        clickedSearchInput.focus({ preventScroll: true });
        const caretPosition = clickedSearchInput.value.length;
        clickedSearchInput.setSelectionRange?.(caretPosition, caretPosition);
      };
      window.requestAnimationFrame(restoreSearchFocus);
      window.setTimeout(restoreSearchFocus, 120);
      return;
    }

    const clearButton = event.target.closest('[data-ppa-boost-search].ppa-search-has-query .search__button');
    if (clearButton) {
      event.preventDefault();
      event.stopPropagation();
      const input = clearButton.closest('[data-ppa-boost-search]')?.querySelector('input[type="search"]');
      if (input) {
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        syncSearchControl(input);
      }
      closeMobileSearch(clearButton);
      return;
    }

    const closeButton = event.target.closest('.ppa-boost-search__close, .ppa-boost-search__panel-close');
    if (closeButton) {
      event.preventDefault();
      event.stopPropagation();
      closeMobileSearch(closeButton);
      return;
    }

    if (
      (searchMenuActive || document.body.classList.contains('ppa-boost-search-open')) &&
      !event.target.closest('[data-ppa-boost-search], #boost-sd__search-widget-init-wrapper[data-ppa-search-panel]')
    ) {
      closeMobileSearch(event.target);
    }
  }, true);

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-ppa-business-price-only]')) {
      setInstantBusinessPriceFilter(event.target.checked);
      return;
    }

    if (event.target.matches('[data-ppa-results-business-price-only]')) {
      setResultsBusinessPriceFilter(event.target.checked);
      enhanceResultsCards();
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    const input = activeInput();
    const isReadyToType =
      document.body.dataset.ppaBoostSearchState === 'ready' &&
      input &&
      document.activeElement !== input &&
      !event.isComposing &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey &&
      event.key.length === 1 &&
      !event.target.closest('input, textarea, select, [contenteditable="true"]');

    if (isReadyToType) {
      event.preventDefault();
      input.focus({ preventScroll: true });
      input.value = `${input.value}${event.key}`;
      input.setSelectionRange?.(input.value.length, input.value.length);
      input.dispatchEvent(
        typeof InputEvent === 'function'
          ? new InputEvent('input', { bubbles: true, inputType: 'insertText', data: event.key })
          : new Event('input', { bubbles: true })
      );
      return;
    }

    if (
      event.key !== 'Escape' ||
      (!searchMenuActive && !document.body.classList.contains('ppa-boost-search-open'))
    ) return;
    closeMobileSearch(event.target);
  });

  window.addEventListener('resize', () => {
    queueUpdate();
    queueResultsEnhancement();
  }, { passive: true });
  window.addEventListener(
    'scroll',
    () => {
      if (!searchMenuActive && !document.body.classList.contains('ppa-boost-search-open')) return;
      queueUpdate();
    },
    { passive: true }
  );

  if (!observeBoostContainers()) {
    discoveryObserver = new MutationObserver(observeBoostContainers);
    discoveryObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (isResultsPage) {
    observeResultsCards();

    resultsRootObserver = new MutationObserver(() => {
      const results = document.querySelector('.boost-sd__filter-product-list');
      const collectionTitleMissing = Boolean(
        document.querySelector('[data-ppa-collection-title]') &&
          !document.querySelector('.collection-hero__title, [data-ppa-results-collection-heading]')
      );

      if (results !== observedResultsRoot || collectionTitleMissing) {
        window.requestAnimationFrame(() => {
          observeResultsCards();
          enhanceResultsCards();
        });
      }
    });
    resultsRootObserver.observe(document.querySelector('#MainContent') || document.body, {
      childList: true,
      subtree: true,
    });
  }
})();
